import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  StatusBadge,
  textColumn,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import { useContractGroups, useDeleteProduct, useProducts } from '../api/contractsApi.ts'
import type { Product } from '../types.ts'
import { ProductFormModal } from '../components/ProductFormModal.tsx'

const CATEGORY_LABELS: Record<Product['category'], string> = {
  license: 'Lizenz',
  device: 'Gerät',
  service: 'Service',
  support: 'Support',
  storage: 'Storage',
  other: 'Sonstiges',
}

export function ProductCatalogAdminSection() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [editing, setEditing] = useState<Product | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Product | null>(null)
  const { data: rows = [], isLoading, error, refetch } = useProducts()
  const { data: contractGroups = [] } = useContractGroups()
  const del = useDeleteProduct()

  const groupNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const g of contractGroups) m.set(g.id, g.name)
    return m
  }, [contractGroups])

  const columns = useMemo<ColumnDef<Product, unknown>[]>(
    () => [
      textColumn<Product>('sku', 'SKU', { size: 120 }),
      textColumn<Product>('name', 'Name'),
      {
        accessorKey: 'category',
        header: 'Kategorie',
        size: 130,
        cell: ({ getValue }) => <Badge variant="neutral" size="sm">{CATEGORY_LABELS[getValue() as Product['category']]}</Badge>,
      },
      {
        accessorKey: 'contractGroupId',
        header: 'Vertragsgruppe',
        size: 160,
        cell: ({ getValue }) => {
          const id = getValue() as string | null
          if (!id) return <span className="text-caption text-gray-400">—</span>
          const name = groupNameById.get(id)
          return <Badge variant="info" size="sm">{name ?? id}</Badge>
        },
      },
      textColumn<Product>('unit', 'Einheit', { size: 100 }),
      {
        accessorKey: 'sapItemCode',
        header: 'SAP-Artikel',
        size: 140,
        cell: ({ getValue }) => {
          const code = getValue() as string | null
          return code
            ? <code className="text-caption text-gray-700">{code}</code>
            : <span className="text-caption text-gray-400">—</span>
        },
      },
      {
        accessorKey: 'defaultSource',
        header: 'Quelle',
        size: 110,
        cell: ({ getValue }) => <Badge variant="info" size="sm">{String(getValue())}</Badge>,
      },
      {
        accessorKey: 'active',
        header: 'Aktiv',
        size: 90,
        cell: ({ getValue }) => (
          <StatusBadge status={getValue() ? 'active' : 'inactive'} />
        ),
      },
      {
        id: '__actions__',
        header: '',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <IconButton
              icon={<Pencil className="w-4 h-4" />}
              aria-label="Bearbeiten"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                setEditing(row.original)
              }}
            />
            <IconButton
              icon={<Trash2 className="w-4 h-4" />}
              aria-label="Löschen"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                setConfirmDelete(row.original)
              }}
            />
          </div>
        ),
      },
    ],
    [groupNameById],
  )

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
          Neues Produkt
        </Button>
      </div>

      <DataTable<Product>
        data={rows}
        columns={columns}
        getRowId={(p) => p.id}
        loading={isLoading}
        error={error ? 'Produktkatalog konnte nicht geladen werden.' : null}
        onRetry={() => void refetch()}
        searchPlaceholder="Produkte suchen …"
        onRowClick={(p) => navigate(`/vertraege/produkte/${p.id}`)}
        emptyState={
          <EmptyState
            title="Noch keine Produkte"
            description="Vertragstypen und Lizenz-/Service-Bausteine sind Produkte. Lege das erste Produkt an, damit du es in einem Vertrag verwenden kannst."
            action={
              <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
                Neues Produkt
              </Button>
            }
          />
        }
      />

      <ProductFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={() => setCreateOpen(false)}
      />
      <ProductFormModal
        open={Boolean(editing)}
        onOpenChange={(o) => !o && setEditing(null)}
        product={editing}
        onSaved={() => setEditing(null)}
      />
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Produkt löschen?"
        message={
          confirmDelete
            ? `„${confirmDelete.name}" wird gelöscht. Das ist nur möglich, wenn es in keinem Vertrag, Tarif oder Bündel mehr verwendet wird.`
            : ''
        }
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast({ title: 'Produkt gelöscht', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) => {
              toast({
                title: 'Löschen nicht möglich',
                description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                tone: 'danger',
              })
            },
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
